import { chain, isDate, range } from "lodash";

import { CrremPathway } from "graphql-types/graphql";
import {
    ReductionAnalysisChartData,
    ReductionProposal,
} from "legacyNext/types";

type NumericKeys<T> = {
    [K in keyof T]: T[K] extends number | null ? K : never;
}[keyof T];

export const findStrandedYear = <
    K extends NumericKeys<ReductionAnalysisChartData>
>(
    intensities: (Pick<ReductionAnalysisChartData, "date"> &
        Partial<Pick<ReductionAnalysisChartData, K>>)[],
    pathways: Pick<CrremPathway, "year" | "value">[],
    keys: K[]
) => {
    return (
        range(2020, 2050).find((year) => {
            const item = intensities.find((i) => i.date.getFullYear() === year);
            if (!item) return false;

            const value = keys
                .map((k) => item[k])
                .find((v) => v && isNaN(v as number) !== true);
            if (!value || isDate(value)) return false;

            const crremValues = pathways.filter((p) => p.year === year);
            return crremValues.some((p) => value > p.value);
        }) ?? "-"
    );
};

export const getValue = (
    data: (Partial<ReductionAnalysisChartData> & { date: Date })[],
    year: number | undefined,
    key: "int" | "proj" | "red"
): number | null =>
    year
        ? data.find(
              (item) =>
                  item.date.getFullYear() === year - (key === "proj" ? 1 : 0)
          )?.[key] ?? null
        : null;

export const buildEpcSummary = (proposals: ReductionProposal[]) => {
    let reductionYear = 0;
    const epcSummary = chain(proposals)
        .groupBy((item) => item.epc)
        .map((item) => {
            const { labels } = item[0];
            const lastStartYear = Math.max(
                ...item.map((item) => item.startYear || 0)
            );
            const lastStartYearString = lastStartYear
                ? lastStartYear.toString()
                : "-";

            reductionYear = Math.max(reductionYear, lastStartYear);

            const investment = chain(item)
                .filter((item) => Boolean(item.enabled && item.startYear))
                .map((item) => Number(item.investment))
                .sum()
                .value();

            return [
                labels.current,
                labels.profitable,
                lastStartYearString,
                investment,
            ] as const;
        })
        .value();

    return {
        epcSummary,
        reductionYear,
    };
};
